const escapeHtml = (value) => String(value ?? "—")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

export const printDetailSheet = (title, fields) => {
  const popup = window.open("", "_blank");
  if (!popup) {
    return;
  }
  popup.opener = null;
  const rows = fields.map(({ label, value }) => `
    <tr>
      <th>${escapeHtml(label)}</th>
      <td>${escapeHtml(value)}</td>
    </tr>
  `).join("");
  popup.document.write(`
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          body { color: #1d2939; font: 14px Arial, sans-serif; margin: 32px; }
          h1 { font-size: 24px; margin: 0 0 24px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border-bottom: 1px solid #d0d5dd; padding: 10px; text-align: left; }
          th { color: #475467; font-weight: 600; width: 34%; }
          td { white-space: pre-wrap; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <table>${rows}</table>
      </body>
    </html>
  `);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 200);
};

const bookingChecklistRows = (checklist) => (checklist || [])
  .map((item) => `
    <tr>
      <td>${escapeHtml(item.item)}</td>
      <td>${escapeHtml([item.required, item.units].filter(Boolean).join(" "))}</td>
      <td>${item.done ? "Prepared" : "To prepare"}</td>
      <td>${item.missing_qty ? `${escapeHtml(item.missing_qty)} missing` : "—"}</td>
    </tr>
  `)
  .join("");

const bookingShortages = (shortages) => (shortages || [])
  .filter((shortage) => !shortage.resolved)
  .map((shortage) => `
    <li>
      ${escapeHtml(`${shortage.qty} × ${shortage.item}`)}
      ${shortage.note ? ` — ${escapeHtml(shortage.note)}` : ""}
    </li>
  `)
  .join("");

export const printBookingSheet = (booking) => {
  const popup = window.open("", "_blank");
  if (!popup) {
    return;
  }
  popup.opener = null;
  const lesson = booking.lesson || {};
  const practical = booking.practical || {};
  const checklist = bookingChecklistRows(booking.checklist);
  const shortages = bookingShortages(booking.shortages);
  popup.document.write(`
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(`${booking.reference} — ${practical.name}`)}</title>
        <style>
          @page { margin: 16mm; }
          body { color: #1d2939; font: 14px Arial, sans-serif; margin: 0; }
          .brand { align-items: center; border-bottom: 2px solid #0b6a5a; display: flex; gap: 10px; padding-bottom: 14px; }
          .mark { align-items: center; background: #0b6a5a; border-radius: 50%; color: white; display: grid; height: 34px; place-items: center; width: 34px; }
          .brand-name { color: #0b6a5a; font-size: 20px; font-weight: 700; }
          .brand-label { color: #667085; font-size: 11px; margin-left: auto; text-transform: uppercase; }
          h1 { font-size: 24px; margin: 22px 0 6px; }
          .reference { color: #475467; font-family: monospace; font-size: 12px; }
          .status { border: 1px solid #98d1c5; border-radius: 999px; color: #075e50; display: inline-block; font-size: 12px; margin-left: 8px; padding: 3px 8px; }
          .meta { display: grid; gap: 16px; grid-template-columns: repeat(3, 1fr); margin: 24px 0; }
          .label { color: #667085; font-size: 10px; letter-spacing: .08em; text-transform: uppercase; }
          .value { font-size: 14px; font-weight: 600; margin-top: 4px; }
          section { margin-top: 24px; }
          h2 { font-size: 15px; margin: 0 0 9px; }
          p { line-height: 1.55; margin: 0; white-space: pre-wrap; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border-bottom: 1px solid #d0d5dd; padding: 9px 8px; text-align: left; }
          th { color: #475467; font-size: 10px; text-transform: uppercase; }
          .shortages { border-left: 3px solid #d92d20; padding-left: 12px; }
          ul { line-height: 1.55; margin: 0; padding-left: 18px; }
        </style>
      </head>
      <body>
        <header class="brand" data-testid="printed-booking-brand">
          <div class="mark" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 2v7.31" />
              <path d="M14 9.3V2" />
              <path d="M8.5 2h7" />
              <path d="M14 9.3a6.5 6.5 0 1 1-4 0" />
              <path d="M5.5 14h13" />
            </svg>
          </div>
          <div class="brand-name">Ask Jonny</div>
          <div class="brand-label">Practical booking</div>
        </header>
        <div class="reference" data-testid="printed-booking-reference">
          ${escapeHtml(booking.reference)}
          <span class="status">${escapeHtml(booking.status)}</span>
        </div>
        <h1>${escapeHtml(practical.name)}</h1>
        <div class="meta">
          <div><div class="label">Teacher</div><div class="value">${escapeHtml(booking.teacher_name)}</div></div>
          <div><div class="label">Class</div><div class="value">${escapeHtml(`${lesson.class_name || ""} (${lesson.pupils || "—"})`)}</div></div>
          <div><div class="label">Room</div><div class="value">${escapeHtml(lesson.lab)}</div></div>
          <div><div class="label">Date</div><div class="value">${escapeHtml(`${lesson.day || ""} ${lesson.date || ""} · Week ${lesson.week || ""}`)}</div></div>
          <div><div class="label">Period</div><div class="value">${escapeHtml(`P${lesson.period || ""} ${lesson.start || ""}–${lesson.end || ""}`)}</div></div>
          <div><div class="label">Sets</div><div class="value">${escapeHtml(booking.sets)}</div></div>
        </div>
        ${practical.prep_notes ? `<section><h2>Prep notes</h2><p>${escapeHtml(practical.prep_notes)}</p></section>` : ""}
        ${booking.notes ? `<section><h2>Teacher notes</h2><p>${escapeHtml(booking.notes)}</p></section>` : ""}
        <section>
          <h2>Preparation checklist</h2>
          <table>
            <thead><tr><th>Item</th><th>Required</th><th>Progress</th><th>Shortage</th></tr></thead>
            <tbody>${checklist}</tbody>
          </table>
        </section>
        ${shortages ? `<section class="shortages"><h2>Open shortages</h2><ul>${shortages}</ul></section>` : ""}
      </body>
    </html>
  `);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 200);
};